/* eslint-disable prefer-arrow-callback */
import { type RefObject, useEffect, useRef, useState } from "react";

/**
 * Client width of the returned ref's element, kept in sync with its resizes. It is 0 until the first
 * measurement lands, so callers must have a sensible fallback for that frame.
 */
export const useMeasuredWidth = <T extends HTMLElement>({ enabled = true }: { enabled?: boolean } = {}): [RefObject<T>, number] => {
  const elementRef = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(
    function observeElementWidth() {
      const element = elementRef.current;
      // Callers that don't use the width skip the observer, so a resize doesn't re-render them.
      if (!element || !enabled) return undefined;

      const syncWidth = () => setWidth(element.clientWidth);

      syncWidth();
      const resizeObserver = new ResizeObserver(syncWidth);
      resizeObserver.observe(element);

      return () => resizeObserver.disconnect();
    },
    [enabled]
  );

  return [elementRef, width];
};
